import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ISingleSpaReceivedMessage, ISingleSpaPostMessage } from '@src/types';
import { sendEventToWebView } from '@src/bridge/bridgeMessenger';
import { EWebViewEvents } from '@src/bridge/types';

interface IAuthenticationContext {
  isLoading: boolean;
  isIframeMounted: boolean;
  isAuthenticated: boolean;
  setIsLoading: (isLoading: boolean) => void;
  setIsIframeMounted: (isIframeMounted: boolean) => void;
  handleRedirect: () => void;
}

const defaultValue: IAuthenticationContext = {
  isLoading: true,
  isIframeMounted: false,
  isAuthenticated: false,

  setIsLoading: () => {},
  setIsIframeMounted: () => {},
  handleRedirect: () => {},
};

const AuthenticationContext = createContext<IAuthenticationContext>(defaultValue);

export const AuthenticationContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isIframeMounted, setIsIframeMounted] = useState<boolean>(false);

  const location = useLocation();
  const navigate = useNavigate();

  const isIframe = useMemo(() => window.self !== window.top, []);

  const isAuthenticated = useMemo(
    () => isIframe && isIframeMounted && !isLoading,
    [isIframe, isIframeMounted, isLoading]
  );

  const handleRedirect = useCallback(
    (route?: string) => {
      if (isIframe) {
        window.parent.postMessage(
          {
            type: route ? 'CHANGE_PARENT_URL' : 'REPLACE_PARENT_URL',
            url: route ?? process.env.REACT_APP_SSO_URL ?? '',
          } as ISingleSpaPostMessage,
          process.env.REACT_APP_ROOT_CONFIG_URL ?? 'http://localhost:3000'
        );
      } else {
        window.location.replace(process.env.REACT_APP_SSO_URL ?? '');
      }
    },
    [isIframe]
  );

  useEffect(() => {
    isAuthenticated && handleRedirect(location.pathname + location.search);
  }, [handleRedirect, isAuthenticated, location]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin === process.env.REACT_APP_ROOT_CONFIG_URL) {
        switch ((event.data as ISingleSpaReceivedMessage).type) {
          case 'MEET_UNMOUNTED':
            setIsLoading(false);
            setIsIframeMounted(false);
            break;
          case 'MEET_MOUNTED':
            setIsLoading(false);
            sendEventToWebView({ eventType: EWebViewEvents.PAGE_FINISHED });
            setIsIframeMounted(true);
            break;
          case 'ROUTE_UPDATED': {
            const path = event.data.path;
            // Only navigate if:
            // 1. Path is provided (not empty/undefined)
            // 2. Iframe is mounted (PAGE_FINISHED has been sent)
            // 3. Route has actually changed
            if (path && path.trim() !== '' && isIframeMounted) {
              if (window.location.pathname + window.location.search !== path) {
                navigate(path);
              }
            }
            break;
          }
          default:
            return;
        }
      }
    };
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [navigate, isIframeMounted]);

  useEffect(() => {
    if (!isIframe) {
      const timeout = setTimeout(() => {
        setIsLoading(false);
      }, 700);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isIframe]);

  const contextValue = useMemo(
    () => ({
      isLoading,
      isIframeMounted,
      isAuthenticated,
      setIsLoading,
      setIsIframeMounted,
      handleRedirect,
    }),
    [handleRedirect, isAuthenticated, isIframeMounted, isLoading]
  );

  return <AuthenticationContext.Provider value={contextValue}>{children}</AuthenticationContext.Provider>;
};

export default AuthenticationContext;
